import React, { useEffect } from 'react';
import { Gauge } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useEditMode } from '../../hooks/useEditMode';
import { CreditScore } from '../../types';

const formatScore = (value: number, privacyMode?: boolean): string => {
  if (privacyMode) return '•••';
  return String(value);
};

interface CreditScoresWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

const getScoreColor = (score: number, darkMode: boolean): string => {
  if (score >= 740) return darkMode ? 'text-green-400' : 'text-green-600';
  if (score >= 670) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
  return darkMode ? 'text-red-400' : 'text-red-600';
};

const getScoreLabel = (score: number): string => {
  if (score >= 740) return 'Excellent';
  if (score >= 670) return 'Good';
  return 'Needs Work';
};

export const CreditScoresWidget: React.FC<CreditScoresWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { creditScores, updateCreditScore, handleDeleteCreditScore, handleBatchDelete } = useFinancialData();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('creditScore');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteCreditScore(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Credit Scores', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, creditScores.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateCreditScore(index, value, sheetId);
    }
  };

  const averageScore = creditScores.length > 0
    ? Math.round(creditScores.reduce((sum, cs) => sum + cs.score, 0) / creditScores.length)
    : 0;

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Credit Scores"
        icon={<Gauge size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={creditScores.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('creditScore')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        {editMode.isEditMode && (
          <EditModeActionBar
            selectedCount={editMode.selectedIndices.size}
            totalCount={creditScores.length}
            allSelected={editMode.selectedIndices.size === creditScores.length}
            onSelectAll={() => editMode.selectAll(creditScores.length)}
            onDeselectAll={editMode.deselectAll}
            onDeleteSelected={editMode.deleteSelected}
            isDeleting={editMode.isDeleting}
          />
        )}

        <div className="overflow-x-auto">
          {creditScores.length === 0 ? (
            <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <p className="text-sm">No credit scores yet</p>
              <p className="text-xs mt-1">Add a score to track your credit health</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                  {editMode.isEditMode && <th className="w-8 pb-2" />}
                  <th className="text-left pb-2">Bureau</th>
                  <th className="text-right pb-2 w-[25%]">Score</th>
                  <th className="text-right pb-2 w-[25%]">Rating</th>
                </tr>
              </thead>
              <tbody>
                {creditScores.map((cs: CreditScore, idx: number) => (
                  <tr
                    key={idx}
                    className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}
                  >
                    {editMode.isEditMode && (
                      <td className="py-2 pr-2">
                        <EditModeCheckbox
                          checked={editMode.selectedIndices.has(idx)}
                          onToggle={() => editMode.toggleIndex(idx)}
                        />
                      </td>
                    )}
                    <td className="py-2 font-semibold">{cs.bureau}</td>
                    <td className="text-right">
                      <div className="inline-block">
                        <InlineEditField
                          value={cs.score}
                          isEditing={isEditing('creditScore', idx, 'score')}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={() => startEdit('creditScore', idx, 'score', cs.score)}
                          onSave={() => handleSave(idx)}
                          onCancel={cancelEdit}
                          editable={cs.editable}
                          variant="neutral"
                          privacyMode={privacyMode}
                          formatFn={formatScore}
                        />
                      </div>
                    </td>
                    <td className={`text-right text-xs font-medium ${getScoreColor(cs.score, darkMode)}`}>
                      {privacyMode ? '•••' : getScoreLabel(cs.score)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>
            Average
          </span>
          <span className={creditScores.length > 0 ? getScoreColor(averageScore, darkMode) : (darkMode ? 'text-slate-400' : 'text-slate-500')}>
            {privacyMode ? '•••' : (creditScores.length > 0 ? averageScore : '—')}
          </span>
        </div>
      </CardFooter>
    </Card>
  );
};
